import React, { useState } from "react"
import { motion } from "framer-motion"
import {Trash2, RotateCcw, AlertTriangle} from 'lucide-react'
import { useGuias } from "../context/GuiasContext"

export default function LixeiraGuias() {
  const { guiasExcluidas, restaurarGuia, deletarPermanentemente } = useGuias()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedGuiaId, setSelectedGuiaId] = useState<string | null>(null)

  const handleRestaurar = async (id: string) => {
    await restaurarGuia(id)
  }

  const handleDeletePermanente = (id: string) => {
    setSelectedGuiaId(id)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (selectedGuiaId) {
      await deletarPermanentemente(selectedGuiaId)
      setShowDeleteModal(false)
      setSelectedGuiaId(null)
    }
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Lixeira de Guias</h2>
        <p className="text-gray-600">Guias excluídas que podem ser restauradas</p>
      </div>

      {guiasExcluidas.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <Trash2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">Lixeira vazia</p>
          <p className="text-gray-400 text-sm mt-2">Nenhuma guia excluída</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#004161] text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Nº Guia</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Nome do Falecido</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Data Óbito</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Data Exclusão</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {guiasExcluidas.map((guia) => (
                  <tr key={guia._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-800">{guia.numero}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{guia.nomeFalecido}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(guia.dataObito).toLocaleDateString("pt-BR")}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {guia.deletedAt ? new Date(guia.deletedAt).toLocaleDateString("pt-BR") : "-"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleRestaurar(guia._id!)}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1 text-sm"
                          title="Restaurar Guia"
                        >
                          <RotateCcw className="w-4 h-4" />
                          Restaurar
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleDeletePermanente(guia._id!)}
                          className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1 text-sm"
                          title="Excluir Permanentemente"
                        >
                          <Trash2 className="w-4 h-4" />
                          Excluir
                        </motion.button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão Permanente */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Confirmar Exclusão Permanente</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Esta ação é <strong>irreversível</strong>! A guia será excluída permanentemente do sistema e não poderá ser recuperada. Deseja continuar?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setSelectedGuiaId(null)
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Excluir Permanentemente
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}
